import * as XLSX from "xlsx";
import type {
  AdAgg,
  AppRules,
  CategoryId,
  CustomerReturnAgg,
  FeePreviewRanges,
  FeePreviewRow,
  FileIssue,
  OrderAgg,
  ProductMapRow,
  StorageFeeRow,
  TransactionAgg
} from "./types";
import {
  cleanText,
  daysInMonth,
  fileKey,
  formatUtcInTimeZone,
  monthKey,
  normalizeHeader,
  parseReportDate,
  toNumber
} from "./utils";

type RowObject = Record<string, unknown>;
const sourceRowNumberKey = "__sourceRowNumber";

interface ParsedObjects {
  rows: RowObject[];
  issues: FileIssue[];
}

interface FileContext {
  category: CategoryId;
  fileName: string;
}

function issue(
  context: FileContext,
  severity: FileIssue["severity"],
  message: string,
  row?: number,
  key?: string
): FileIssue {
  return { severity, category: context.category, fileName: context.fileName, message, row, key };
}

async function decodeText(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const utf8 = new TextDecoder("utf-8").decode(buffer);
  if (!utf8.includes("\uFFFD")) return utf8;
  try {
    return new TextDecoder("gb18030").decode(buffer);
  } catch {
    return utf8;
  }
}

function parseDelimited(text: string): string[][] {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  const delimiter = firstLine.includes("\t") ? "\t" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      row.push(cell);
      cell = "";
    } else if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(cell);
      if (row.some((v) => cleanText(v))) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }

  row.push(cell);
  if (row.some((v) => cleanText(v))) rows.push(row);
  return rows;
}

async function readRows(file: File): Promise<string[][]> {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (["xlsx", "xls"].includes(ext)) {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: false });
    const firstSheet = workbook.SheetNames[0];
    if (!firstSheet) return [];
    const sheet = workbook.Sheets[firstSheet];
    return XLSX.utils.sheet_to_json<string[]>(sheet, {
      header: 1,
      defval: "",
      raw: false
    }) as string[][];
  }
  return parseDelimited(await decodeText(file));
}

function headerMap(header: unknown[]): Map<string, number> {
  const map = new Map<string, number>();
  header.forEach((value, index) => {
    const key = normalizeHeader(value);
    if (key && !map.has(key)) map.set(key, index);
  });
  return map;
}

function findHeaderIndex(rows: string[][], required: string[]): number {
  const normalizedRequired = required.map(normalizeHeader);
  return rows.findIndex((row) => {
    const map = headerMap(row);
    return normalizedRequired.every((key) => map.has(key));
  });
}

function rowsToObjects(rows: string[][], context: FileContext, required: string[]): ParsedObjects {
  const headerIndex = findHeaderIndex(rows, required);
  if (headerIndex < 0) {
    return {
      rows: [],
      issues: [
        issue(
          context,
          "error",
          `文件表头不匹配，缺少字段：${required.join(", ")}`
        )
      ]
    };
  }

  const header = rows[headerIndex];
  const keys = header.map((value, index) => cleanText(value) || `Column${index + 1}`);
  const dataRows = rows.slice(headerIndex + 1);
  const objects = dataRows
    .filter((row) => row.some((v) => cleanText(v)))
    .map((row, dataIndex) => {
      const obj: RowObject = {};
      keys.forEach((key, index) => {
        obj[key] = row[index] ?? "";
      });
      obj[sourceRowNumberKey] = headerIndex + dataIndex + 2;
      return obj;
    });
  return { rows: objects, issues: [] };
}

function get(row: RowObject, names: string[]): unknown {
  const normalized = new Map<string, unknown>();
  Object.entries(row).forEach(([key, value]) => normalized.set(normalizeHeader(key), value));
  for (const name of names) {
    const value = normalized.get(normalizeHeader(name));
    if (value !== undefined) return value;
  }
  return "";
}

function sourceRowNumber(row: RowObject, fallback: number): number {
  const value = row[sourceRowNumberKey];
  return typeof value === "number" ? value : fallback;
}

function addToMap<T>(map: Map<string, T>, key: string, factory: () => T): T {
  const existing = map.get(key);
  if (existing) return existing;
  const created = factory();
  map.set(key, created);
  return created;
}

export async function parseProductMap(files: File[]): Promise<{
  products: ProductMapRow[];
  issues: FileIssue[];
}> {
  const issues: FileIssue[] = [];
  const products: ProductMapRow[] = [];
  for (const file of files) {
    const context: FileContext = { category: "productMap", fileName: file.name };
    const parsed = rowsToObjects(await readRows(file), context, ["asin", "sku"]);
    issues.push(...parsed.issues);
    for (const row of parsed.rows) {
      const asin = cleanText(get(row, ["asin"]));
      const sku = cleanText(get(row, ["sku"]));
      if (!asin || !sku) continue;
      products.push({
        channel: cleanText(get(row, ["channle", "channel"])) || "未分组",
        listingName: cleanText(get(row, ["listing name", "product-name", "product_name"])),
        asin,
        sku,
        landingCostUsd: toNumber(get(row, ["落地成本（$）", "落地成本($)", "landed cost", "landing cost"]))
      });
    }
  }
  if (!products.length) {
    issues.push({
      severity: "error",
      category: "productMap",
      message: "对应表没有解析到任何 ASIN/SKU。"
    });
  }
  return { products, issues };
}

export async function parseAllOrders(
  files: File[],
  rules: AppRules
): Promise<{ orders: Map<string, OrderAgg>; orderCount: number; issues: FileIssue[]; dates: string[] }> {
  const issues: FileIssue[] = [];
  const orders = new Map<string, OrderAgg>();
  const seenOrderLines = new Set<string>();
  const dates: string[] = [];

  for (const file of files) {
    const context: FileContext = { category: "allOrder", fileName: file.name };
    const parsed = rowsToObjects(await readRows(file), context, [
      "amazon-order-id",
      "purchase-date",
      "order-status",
      "sales-channel",
      "sku",
      "asin",
      "quantity",
      "item-price"
    ]);
    issues.push(...parsed.issues);
    parsed.rows.forEach((row, index) => {
      const status = cleanText(get(row, ["order-status"]));
      const salesChannel = cleanText(get(row, ["sales-channel"]));
      if (status === "Cancelled" || salesChannel !== "Amazon.com") return;
      const asin = cleanText(get(row, ["asin"]));
      const sku = cleanText(get(row, ["sku"]));
      const orderId = cleanText(get(row, ["amazon-order-id"]));
      const date = formatUtcInTimeZone(get(row, ["purchase-date"]), rules.timezone);
      if (!asin || !sku || !date) return;
      const dedupeKey = `${orderId}|${sku}|${asin}|${date}`;
      if (seenOrderLines.has(dedupeKey)) {
        issues.push(issue(context, "warning", "重复订单行已忽略。", sourceRowNumber(row, index + 2), dedupeKey));
        return;
      }
      seenOrderLines.add(dedupeKey);
      const quantity = toNumber(get(row, ["quantity"]));
      const itemPrice = toNumber(get(row, ["item-price"]));
      const promotion = toNumber(get(row, ["item-promotion-discount"]));
      const sales = itemPrice - promotion;
      const key = `${date}|${asin}|${sku}`;
      const agg = addToMap(orders, key, () => ({
        date,
        asin,
        sku,
        sales: 0,
        quantity: 0,
        orderIds: new Set<string>()
      }));
      agg.sales += sales;
      agg.quantity += quantity;
      if (orderId) agg.orderIds.add(orderId);
      dates.push(date);
    });
  }

  return { orders, orderCount: seenOrderLines.size, issues, dates };
}

export async function parseFeePreview(files: File[], ranges: FeePreviewRanges = {}): Promise<{
  fees: FeePreviewRow[];
  issues: FileIssue[];
}> {
  const issues: FileIssue[] = [];
  const fees: FeePreviewRow[] = [];
  for (const [fileIndex, file] of files.entries()) {
    const context: FileContext = { category: "feePreview", fileName: file.name };
    const key = fileKey(file);
    const range = ranges[key] ?? { startDate: "", endDate: "" };
    if (range.startDate && range.endDate && range.startDate > range.endDate) {
      issues.push(issue(context, "error", "Fee Preview 适用日期范围无效：开始日期晚于结束日期。"));
    }
    const parsed = rowsToObjects(await readRows(file), context, [
      "asin",
      "expected-fulfillment-fee-per-unit"
    ]);
    issues.push(...parsed.issues);
    for (const row of parsed.rows) {
      const asin = cleanText(get(row, ["asin"]));
      const sku = cleanText(get(row, ['?"sku"', "sku"]));
      const feePerUnit = toNumber(get(row, ["expected-fulfillment-fee-per-unit"]));
      if (!asin || !feePerUnit) continue;
      fees.push({
        asin,
        sku,
        feePerUnit,
        fileKey: key,
        fileName: file.name,
        fileIndex,
        startDate: range.startDate,
        endDate: range.endDate
      });
    }
  }
  return { fees, issues };
}

export async function parseSpAds(files: File[]): Promise<{
  ads: Map<string, AdAgg>;
  issues: FileIssue[];
  dates: string[];
}> {
  const issues: FileIssue[] = [];
  const ads = new Map<string, AdAgg>();
  const dates: string[] = [];
  for (const file of files) {
    const context: FileContext = { category: "spAds", fileName: file.name };
    const parsed = rowsToObjects(await readRows(file), context, [
      "Date",
      "Advertised ASIN",
      "Impressions",
      "Clicks",
      "Spend"
    ]);
    issues.push(...parsed.issues);
    for (const row of parsed.rows) {
      const date = parseReportDate(get(row, ["Date"]));
      const asin = cleanText(get(row, ["Advertised ASIN"]));
      if (!date || !asin) continue;
      const key = `${date}|${asin}`;
      const agg = addToMap(ads, key, () => emptyAdAgg(date, asin));
      agg.impressions += toNumber(get(row, ["Impressions"]));
      agg.clicks += toNumber(get(row, ["Clicks"]));
      agg.spend += toNumber(get(row, ["Spend"]));
      agg.adOrders += toNumber(get(row, ["7 Day Total Orders (#)"]));
      agg.adUnits += toNumber(get(row, ["7 Day Total Units (#)"]));
      agg.adSkuUnits += toNumber(get(row, ["7 Day Advertised SKU Units (#)"]));
      agg.adOtherSkuUnits += toNumber(get(row, ["7 Day Other SKU Units (#)"]));
      agg.adSkuSales += toNumber(get(row, ["7 Day Advertised SKU Sales "]));
      agg.adOtherSkuSales += toNumber(get(row, ["7 Day Other SKU Sales "]));
      agg.adSales += toNumber(get(row, ["7 Day Total Sales "]));
      dates.push(date);
    }
  }
  return { ads, issues, dates };
}

export async function parseSbAds(
  files: File[],
  rules: AppRules,
  skuToProduct: Map<string, ProductMapRow>
): Promise<{ ads: Map<string, AdAgg>; issues: FileIssue[]; dates: string[] }> {
  const issues: FileIssue[] = [];
  const ads = new Map<string, AdAgg>();
  const dates: string[] = [];
  for (const file of files) {
    const context: FileContext = { category: "sbAds", fileName: file.name };
    const parsed = rowsToObjects(await readRows(file), context, [
      "Date",
      "Campaign Name",
      "Impressions",
      "Clicks",
      "Spend"
    ]);
    issues.push(...parsed.issues);
    parsed.rows.forEach((row, index) => {
      const spend = toNumber(get(row, ["Spend"]));
      if (!spend) return;
      const date = parseReportDate(get(row, ["Date"]));
      const campaign = cleanText(get(row, ["Campaign Name"]));
      const sku = campaign.includes(rules.sbSkuDelimiter)
        ? campaign.split(rules.sbSkuDelimiter)[0].trim()
        : campaign.trim();
      const product = skuToProduct.get(sku);
      if (!product) {
        issues.push(issue(context, "warning", "SB Campaign Name 无法映射到对应表 SKU。", sourceRowNumber(row, index + 2), sku));
        return;
      }
      const key = `${date}|${product.asin}`;
      const agg = addToMap(ads, key, () => emptyAdAgg(date, product.asin));
      agg.impressions += toNumber(get(row, ["Impressions"]));
      agg.clicks += toNumber(get(row, ["Clicks"]));
      agg.spend += spend;
      agg.adOrders += toNumber(get(row, ["14 Day Total Orders (#)"]));
      agg.adUnits += toNumber(get(row, ["14 Day Total Units (#)"]));
      agg.adSales += toNumber(get(row, ["14 Day Total Sales "]));
      dates.push(date);
    });
  }
  return { ads, issues, dates };
}

export async function parseSdAds(files: File[]): Promise<{
  ads: Map<string, AdAgg>;
  issues: FileIssue[];
  dates: string[];
}> {
  const issues: FileIssue[] = [];
  const ads = new Map<string, AdAgg>();
  const dates: string[] = [];
  for (const file of files) {
    const context: FileContext = { category: "sdAds", fileName: file.name };
    const parsed = rowsToObjects(await readRows(file), context, [
      "Date",
      "Advertised ASIN",
      "Impressions",
      "Clicks",
      "Spend"
    ]);
    issues.push(...parsed.issues);
    for (const row of parsed.rows) {
      const date = parseReportDate(get(row, ["Date"]));
      const asin = cleanText(get(row, ["Advertised ASIN"]));
      if (!date || !asin) continue;
      const key = `${date}|${asin}`;
      const agg = addToMap(ads, key, () => emptyAdAgg(date, asin));
      agg.impressions += toNumber(get(row, ["Impressions"]));
      agg.clicks += toNumber(get(row, ["Clicks"]));
      agg.spend += toNumber(get(row, ["Spend"]));
      agg.adOrders += toNumber(get(row, ["14 Day Total Orders (#)"]));
      agg.adUnits += toNumber(get(row, ["14 Day Total Units (#)"]));
      agg.adSales += toNumber(get(row, ["14 Day Total Sales "]));
      dates.push(date);
    }
  }
  return { ads, issues, dates };
}

function emptyAdAgg(date: string, asin: string): AdAgg {
  return {
    date,
    asin,
    impressions: 0,
    clicks: 0,
    spend: 0,
    adOrders: 0,
    adUnits: 0,
    adSkuUnits: 0,
    adOtherSkuUnits: 0,
    adSkuSales: 0,
    adOtherSkuSales: 0,
    adSales: 0
  };
}

export async function parseStorageFees(files: File[]): Promise<{
  storageFees: StorageFeeRow[];
  issues: FileIssue[];
}> {
  const issues: FileIssue[] = [];
  const grouped = new Map<string, { asin: string; month: string; monthlyFee: number; dayTotal: number; count: number }>();
  for (const file of files) {
    const context: FileContext = { category: "storageFee", fileName: file.name };
    const parsed = rowsToObjects(await readRows(file), context, [
      "asin",
      "month_of_charge",
      "estimated_monthly_storage_fee"
    ]);
    issues.push(...parsed.issues);
    for (const row of parsed.rows) {
      const asin = cleanText(get(row, ["asin"]));
      const date = parseReportDate(get(row, ["month_of_charge"]));
      const month = date ? monthKey(date) : "";
      if (!asin || !month) continue;
      const monthlyFee = toNumber(get(row, ["estimated_monthly_storage_fee"]));
      const key = `${asin}|${month}`;
      const group = addToMap(grouped, key, () => ({
        asin,
        month,
        monthlyFee: 0,
        dayTotal: 0,
        count: 0
      }));
      group.monthlyFee += monthlyFee;
      group.dayTotal += daysInMonth(month);
      group.count += 1;
    }
  }
  const storageFees = Array.from(grouped.values()).map((row) => {
    const days = row.count ? row.dayTotal / row.count : daysInMonth(row.month);
    return {
      asin: row.asin,
      month: row.month,
      monthlyFee: row.monthlyFee,
      daysInMonth: days,
      dailyFee: days ? row.monthlyFee / days : 0
    };
  });
  return { storageFees, issues };
}

export async function parseTransactions(
  files: File[],
  skuToProduct: Map<string, ProductMapRow>
): Promise<{
  transactions: Map<string, TransactionAgg>;
  issues: FileIssue[];
  dates: string[];
  fileCount: number;
  rowCount: number;
}> {
  const issues: FileIssue[] = [];
  const transactions = new Map<string, TransactionAgg>();
  const dates: string[] = [];
  let rowCount = 0;
  let fileCount = 0;
  for (const file of files) {
    const context: FileContext = { category: "transaction", fileName: file.name };
    const parsed = rowsToObjects(await readRows(file), context, ["date/time", "type", "sku", "total"]);
    issues.push(...parsed.issues);
    if (parsed.rows.length) fileCount += 1;
    parsed.rows.forEach((row, index) => {
      rowCount += 1;
      const type = cleanText(get(row, ["type"]));
      if (!["Order", "Refund", "Adjustment"].includes(type)) return;
      const sku = cleanText(get(row, ["sku"]));
      const date = parseReportDate(get(row, ["date/time"]));
      if (!date || !sku) return;
      const product = skuToProduct.get(sku);
      if (!product) {
        issues.push(
          issue(
            context,
            "warning",
            "Transaction SKU 在对应表中不存在，无法映射 ASIN/渠道。",
            sourceRowNumber(row, index + 2),
            sku
          )
        );
        return;
      }
      const key = `${date}|${product.asin}|${sku}`;
      const agg = addToMap(transactions, key, () => ({
        date,
        asin: product.asin,
        sku,
        actualFbaFee: 0,
        actualSellingFee: 0,
        adjustment: 0,
        refundQuantity: 0,
        refundAmount: 0
      }));
      if (type === "Order") {
        agg.actualFbaFee += Math.max(0, -toNumber(get(row, ["fba fees"])));
        agg.actualSellingFee += Math.max(0, -toNumber(get(row, ["selling fees"])));
      } else if (type === "Adjustment") {
        agg.adjustment += toNumber(get(row, ["total"]));
      } else if (type === "Refund") {
        agg.refundQuantity += Math.abs(toNumber(get(row, ["quantity"])));
        agg.refundAmount += toNumber(get(row, ["total"]));
      }
      dates.push(date);
    });
  }
  return { transactions, issues, dates, fileCount, rowCount };
}

export async function parseCustomerReturns(
  files: File[],
  skuToProduct: Map<string, ProductMapRow>,
  asinToProduct: Map<string, ProductMapRow>
): Promise<{ returns: Map<string, CustomerReturnAgg>; issues: FileIssue[]; dates: string[] }> {
  const issues: FileIssue[] = [];
  const returns = new Map<string, CustomerReturnAgg>();
  const dates: string[] = [];
  for (const file of files) {
    const context: FileContext = { category: "customerReturn", fileName: file.name };
    const parsed = rowsToObjects(await readRows(file), context, [
      "return-date",
      "sku",
      "asin",
      "quantity",
      "detailed-disposition"
    ]);
    issues.push(...parsed.issues);
    parsed.rows.forEach((row, index) => {
      const disposition = cleanText(get(row, ["detailed-disposition"]));
      if (disposition !== "SELLABLE") return;
      const sku = cleanText(get(row, ["sku"]));
      const asin = cleanText(get(row, ["asin"]));
      const date = parseReportDate(get(row, ["return-date"]));
      if (!date || !asin) return;
      const product = skuToProduct.get(sku) ?? asinToProduct.get(asin);
      if (!product) {
        issues.push(issue(context, "warning", "Customer Return 无法映射到对应表。", sourceRowNumber(row, index + 2), sku || asin));
        return;
      }
      const quantity = Math.abs(toNumber(get(row, ["quantity"])));
      const key = `${date}|${product.asin}|${product.sku}`;
      const agg = addToMap(returns, key, () => ({
        date,
        asin: product.asin,
        sku: product.sku,
        sellableReturnQuantity: 0,
        returnLandingCost: 0
      }));
      agg.sellableReturnQuantity += quantity;
      agg.returnLandingCost += quantity * product.landingCostUsd;
      dates.push(date);
    });
  }
  return { returns, issues, dates };
}
